import { useState } from "react";
import { api, API, auth } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Layers, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { v: "video", label: "Video URL (YouTube / Vimeo / mp4)" },
  { v: "link",  label: "External Link (Article, Drive…)" },
  { v: "pdf",   label: "PDF (upload or URL)" },
  { v: "ppt",   label: "PPT / Slides (upload or URL)" },
];

const UNIT_TO_SEC = { sec: 1, min: 60, hr: 3600 };
function durationDisplay(secs, unit) {
  const div = UNIT_TO_SEC[unit] || 1;
  return Math.max(1, Math.round((secs || 0) / div));
}
function toSeconds(raw, unit) {
  return Math.max(1, raw) * (UNIT_TO_SEC[unit] || 1);
}

export default function AssessmentFlowCard({ q, setQ }) {
  const enabled = !!q.assessment_flow_enabled;
  const asset = q.training_asset || { type: "video", url: "", storage_path: "", name: "" };
  const [uploading, setUploading] = useState(false);

  const patch = (p) => setQ({ ...q, ...p });
  const patchAsset = (p) => patch({ training_asset: { ...asset, ...p } });

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["pdf", "ppt", "pptx"].some((x) => (file.name || "").toLowerCase().endsWith("." + x))) {
      toast.error("Only PDF or PPT/PPTX allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token()}` },
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json())?.detail || "Upload failed");
      const data = await r.json();
      patchAsset({ storage_path: data.path, url: "", name: file.name });
      toast.success("Training file uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/40 p-4 space-y-4" data-testid="assessment-flow-card">
      {/* Always-visible: Question shuffle toggle */}
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-violet-200/70">
        <div>
          <div className="font-bold text-sm text-slate-900">Shuffle question order</div>
          <div className="text-xs text-slate-500">When ON, every attempt gets a randomly shuffled question order (deters answer-sharing).</div>
        </div>
        <Switch checked={!!q.shuffle_questions} onCheckedChange={(v) => patch({ shuffle_questions: v })} data-testid="shuffle-toggle"/>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Layers className="h-5 w-5 text-violet-600 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-slate-900">Sequential Assessment Flow</div>
            <div className="text-xs text-slate-500 max-w-md">Pre-Test → mandatory Training → Post-Test. Configure attempts, training material, and the post-test window.</div>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={(v) => patch({ assessment_flow_enabled: v })} data-testid="flow-toggle" />
      </div>

      {enabled && (
        <div className="space-y-4 pt-2 border-t border-violet-200/70">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Pre-Test attempts</Label>
              <Input type="number" min={1} max={20} value={q.pre_test_max_attempts}
                onChange={(e) => patch({ pre_test_max_attempts: Math.max(1, parseInt(e.target.value || "1")) })}
                data-testid="pre-attempts-input"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Post-Test attempts</Label>
              <Input type="number" min={1} max={20} value={q.post_test_max_attempts}
                onChange={(e) => patch({ post_test_max_attempts: Math.max(1, parseInt(e.target.value || "1")) })}
                data-testid="post-attempts-input"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Post-Test window (days)</Label>
              <Input type="number" min={1} max={365} value={q.post_test_window_days}
                onChange={(e) => patch({ post_test_window_days: Math.max(1, parseInt(e.target.value || "1")) })}
                data-testid="post-window-input"/>
              <div className="text-[10px] text-slate-400 mt-0.5">After this window → still allowed but flagged &ldquo;Late&rdquo;</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Minimum training duration</Label>
              <div className="flex gap-2">
                <Input type="number" min={1} max={99999}
                  value={durationDisplay(q.training_min_duration_sec, q.training_min_duration_unit || "sec")}
                  onChange={(e) => {
                    const unit = q.training_min_duration_unit || "sec";
                    const raw = Math.max(1, parseInt(e.target.value || "1"));
                    patch({ training_min_duration_sec: toSeconds(raw, unit), training_min_duration_unit: unit });
                  }}
                  data-testid="training-min-input" className="flex-1"/>
                <Select value={q.training_min_duration_unit || "sec"} onValueChange={(u) => {
                  // Convert existing seconds into the new unit for display
                  patch({ training_min_duration_unit: u });
                }}>
                  <SelectTrigger className="w-[110px]" data-testid="training-min-unit"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sec">Seconds</SelectItem>
                    <SelectItem value="min">Minutes</SelectItem>
                    <SelectItem value="hr">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">Stored as {q.training_min_duration_sec}s. Content must stay open at least this long.</div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Training material type</Label>
              <Select value={asset.type || "video"} onValueChange={(v) => patchAsset({ type: v })}>
                <SelectTrigger data-testid="asset-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {(asset.type === "video" || asset.type === "link") && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                {asset.type === "video" ? "Video URL" : "Link URL"}
              </Label>
              <Input value={asset.url || ""} onChange={(e) => patchAsset({ url: e.target.value, storage_path: "" })}
                placeholder={asset.type === "video" ? "https://www.youtube.com/watch?v=…" : "https://…"}
                data-testid="asset-url-input"/>
            </div>
          )}

          {(asset.type === "pdf" || asset.type === "ppt") && (
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">{asset.type.toUpperCase()} — upload OR paste a URL</Label>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-violet-200 text-sm cursor-pointer hover:bg-violet-50">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
                  {uploading ? "Uploading…" : "Choose file"}
                  <input type="file" accept=".pdf,.ppt,.pptx" className="hidden" onChange={onFile} disabled={uploading} data-testid="asset-file-input"/>
                </label>
                {asset.storage_path && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 gap-1">
                    {asset.name || asset.storage_path.split("/").pop()}
                    <button type="button" onClick={() => patchAsset({ storage_path: "", name: "" })} className="ml-1"><X className="h-3 w-3"/></button>
                  </Badge>
                )}
                <span className="text-xs text-slate-400">or</span>
                <Input value={asset.url || ""} onChange={(e) => patchAsset({ url: e.target.value, storage_path: "", name: "" })}
                  placeholder="https://…/file.pdf" className="flex-1 min-w-[200px]" data-testid="asset-url-input"/>
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white/70 border border-violet-200 p-3 text-[12px] text-slate-600 leading-relaxed">
            <span className="font-bold text-violet-700">How it works:</span> Employee enters HCM ID + DOB on the QR/quiz page, sees a 3-step ladder.
            Pre-Test unlocks first. Training opens after at least 1 completed Pre attempt and must stay open ≥ {q.training_min_duration_sec || 60}s.
            Post-Test unlocks after training completes and stays open for {q.post_test_window_days || 7} day(s). Exhausted attempts trigger a one-click reset request to admin.
          </div>
        </div>
      )}
    </div>
  );
}
